import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi, type CleaningScopeResponse, type PropertyGroupResponse } from '@/shared/api';
import {
  CLEANING_SCOPE_STORAGE,
  readScope,
  useOptionalObjectScope,
  useScopeStorageKey,
  writeScope,
} from '@/shared/object-scope';

function fromObjectScope(group: PropertyGroupResponse): CleaningScopeResponse {
  return {
    id: group.id,
    name: group.name,
    timezone: group.timezone,
    apartments: group.apartments.map((apartment) => ({
      id: apartment.id,
      name: apartment.name,
      internalName: apartment.adminDisplayName ?? null,
    })),
  };
}

export function useCleaningScope() {
  const api = useApi();
  const objectScope = useOptionalObjectScope();
  const scopesQuery = useQuery({
    queryKey: ['cleaning', 'scopes'],
    queryFn: api.cleaning.scopes,
    enabled: objectScope === null,
    staleTime: 60_000,
  });
  const storageKey = useScopeStorageKey(CLEANING_SCOPE_STORAGE);
  const [localGroupId, setLocalGroupId] = useState<string | null>(() => readScope(storageKey));
  const localGroups = useMemo(() => scopesQuery.data ?? [], [scopesQuery.data]);
  const localGroup = localGroups.find((group) => group.id === localGroupId) ?? null;
  const setLocalGroup = useCallback(
    (id: string | null) => {
      setLocalGroupId(id);
      writeScope(storageKey, id);
    },
    [storageKey],
  );
  const retryLocal = useCallback(() => {
    void scopesQuery.refetch();
  }, [scopesQuery]);
  useEffect(() => {
    if (localGroupId && scopesQuery.data && !localGroup) setLocalGroup(null);
  }, [localGroupId, localGroup, scopesQuery.data, setLocalGroup]);

  return useMemo(() => {
    if (objectScope) {
      return {
        groups: objectScope.groups.map(fromObjectScope),
        isLoading: objectScope.isLoading,
        isError: objectScope.isError,
        group: objectScope.group ? fromObjectScope(objectScope.group) : null,
        setGroupId: objectScope.setGroupId,
        retry: objectScope.retry,
        usesLocalScope: false,
      };
    }
    return {
      groups: localGroups,
      isLoading: scopesQuery.isLoading,
      isError: scopesQuery.isError,
      group: localGroup,
      setGroupId: setLocalGroup,
      retry: retryLocal,
      usesLocalScope: true,
    };
  }, [
    localGroup,
    localGroups,
    objectScope,
    retryLocal,
    scopesQuery.isError,
    scopesQuery.isLoading,
    setLocalGroup,
  ]);
}
